import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { API_Domain, API_PATHS, type ApiPathKeys, MAX_RETRIES } from "../constants/api-paths";
import type { APIRequestParams, IResponse } from "../types/api.types";
import { getCookie, refreshCSRF, waitForCSRF } from "./csrf.manager";
import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";

// Re-export IResponse for service files
export type { IResponse } from "../types/api.types";

/**
 * Framework-agnostic utility to get access token
 * Can be overridden per framework (React, Solid, etc.)
 */
let getAccessTokenFn = (): string | null => {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(COMMON_SSO_ACCESS_TOKEN);
};

/**
 * Set custom access token getter
 * Allows frameworks to provide their own implementation
 */
export const setAccessTokenGetter = (fn: () => string | null): void => {
  getAccessTokenFn = fn;
};

/**
 * Add Access Token to Request Data
 * Adds access token from localStorage if available
 */
export const addAccessTokenToData = <T extends { mobileNo?: string }>(data?: T): T => {
  try {
    if (!data) return {} as T;

    const accessToken = getAccessTokenFn();

    return {
      ...data,
      ...(accessToken ? { accessToken } : {}),
    };
  } catch {
    return data ?? ({} as T);
  }
};

/**
 * Set Token Expiry
 * Updates token expiry time in localStorage
 */
export const setTokenExpiry = (): void => {
  if (typeof localStorage === "undefined" || typeof document === "undefined") return;

  const isLoggedIn = getCookie("_lsFlag");
  const cookie = getCookie("checkCookie");

  // Skip if the user isn't logged in
  if (!isLoggedIn) {
    localStorage.removeItem("tokenExpiry");
    return;
  }

  const expiry = new Date(+cookie * 1000);
  expiry.setMilliseconds(expiry.getMilliseconds() + 600000);

  // Store token expiry time to localStorage
  localStorage.setItem("tokenExpiry", JSON.stringify(+expiry));
};

/**
 * Get API Path Key
 * Finds the API path key from URL
 */
const getApiPathKey = (url: string): ApiPathKeys | undefined => {
  return Object.entries(API_PATHS).find(([, value]) => value === url)?.[0] as
    ApiPathKeys | undefined;
};

/**
 * Build a fallback error response from an AxiosError
 */
const buildAxiosErrorResponse = <TResponse>(err: AxiosError): IResponse<TResponse> => {
  const fallback: IResponse<TResponse> = {
    status: "error",
    statusCode: err.response?.status ?? 500,
    message: err.message,
    data: null as TResponse,
  };
  return (err.response?.data as IResponse<TResponse>) ?? fallback;
};

/**
 * Resolve the CSRF token – use cached cookie when available, otherwise fetch a fresh one
 */
const resolveCsrfToken = (): Promise<string> => {
  const cookieToken = getCookie("CSRFtoken");
  return cookieToken ? Promise.resolve(cookieToken) : waitForCSRF();
};

/**
 * Standard API Request
 * Makes a standard API request with CSRF token
 *
 * @template TResponse - Response data type
 * @template TRequest - Request data type
 * @param params - Request parameters
 * @returns Promise resolving to API response
 */
export const request = async <TResponse, TRequest = unknown>(
  params: APIRequestParams<TRequest>
): Promise<IResponse<TResponse>> => {
  const { method, url, data, signal, headers = {} } = params;
  const { CancelToken } = axios;
  const source = CancelToken.source();
  let numberOfRetries = 0;
  let numberOfRetriesOnError = 0;

  const handleRequestError = async (
    err: unknown,
    csrfToken: string,
    retry: (token: string) => Promise<IResponse<TResponse>>
  ): Promise<IResponse<TResponse>> => {
    if ((err as AxiosError)?.response?.status === 403 && numberOfRetries < MAX_RETRIES) {
      numberOfRetries++;
      const newToken = await refreshCSRF();
      return retry(newToken);
    }
    if ((err as IResponse<TResponse>)?.statusCode === 55 && numberOfRetriesOnError < MAX_RETRIES) {
      numberOfRetriesOnError++;
      return retry(csrfToken);
    }
    if (err instanceof AxiosError) {
      return buildAxiosErrorResponse<TResponse>(err);
    }
    return err as IResponse<TResponse>;
  };

  const callAPI = async (csrfToken: string): Promise<IResponse<TResponse>> => {
    try {
      const fullUrl = API_Domain ? `${API_Domain}${url}` : url;
      const config: AxiosRequestConfig = {
        method,
        url: fullUrl,
        data: { ...data, _csrf: csrfToken },
        headers: { "Content-Type": "application/json", ...headers },
        signal,
        withCredentials: true,
        cancelToken: source.token,
      };

      const response = await axios(config);
      const res = response.data as IResponse<TResponse>;

      if (res.status === "success") {
        setTokenExpiry();
        return res;
      }
      if (res.status === "error") {
        throw res;
      }
      return res;
    } catch (err) {
      return handleRequestError(err, csrfToken, callAPI);
    }
  };

  const csrfToken = await resolveCsrfToken();
  return callAPI(csrfToken);
};

/**
 * Encrypted API Request
 * Makes an encrypted API request with CSRF token
 * Uses encryption/decryption for sensitive data
 *
 * @template TResponse - Response data type
 * @template TRequest - Request data type
 * @param params - Request parameters
 * @returns Promise resolving to API response
 */
export const encryptedRequest = async <TResponse, TRequest = unknown>(
  params: APIRequestParams<TRequest>
): Promise<IResponse<TResponse>> => {
  const { method, url, data, signal, headers = {} } = params;

  // Import crypto utilities dynamically to avoid circular dependencies
  const { encrypt, decrypt, generateSalt } = await import("@repo/shared-utils/crypto");

  const modifiedData = addAccessTokenToData(data as { mobileNo?: string } | undefined);
  let numberOfRetries = 0;
  let numberOfRetriesOnError = 0;

  const salt: string = await generateSalt();
  const encryptedData: string | undefined = await encrypt(JSON.stringify(modifiedData), salt);

  const decryptResponse = async (response: { data: unknown }): Promise<IResponse<TResponse>> => {
    if (typeof response.data === "string") {
      const decryptedData = await decrypt(response.data, salt);

      return (
        typeof decryptedData === "string" ? JSON.parse(decryptedData) : decryptedData
      ) as IResponse<TResponse>;
    }

    return response.data as IResponse<TResponse>;
  };

  const handleEncryptedError = async (
    err: unknown,
    csrfToken: string,
    retry: (token: string) => Promise<IResponse<TResponse>>
  ): Promise<IResponse<TResponse>> => {
    if ((err as AxiosError)?.response?.status === 403 && numberOfRetries < MAX_RETRIES) {
      numberOfRetries++;
      const token = await refreshCSRF();
      return retry(token);
    }
    if ((err as IResponse<TResponse>)?.statusCode === 55 && numberOfRetriesOnError < MAX_RETRIES) {
      numberOfRetriesOnError++;
      return retry(csrfToken);
    }
    if (
      err instanceof AxiosError &&
      err.code === "ERR_NETWORK" &&
      numberOfRetriesOnError < MAX_RETRIES
    ) {
      numberOfRetriesOnError++;
      return retry(csrfToken);
    }
    if (err instanceof AxiosError) {
      return buildAxiosErrorResponse<TResponse>(err);
    }
    return err as IResponse<TResponse>;
  };

  const callAPI = async (csrfToken: string): Promise<IResponse<TResponse>> => {
    try {
      const fullUrl = API_Domain ? `${API_Domain}${url}` : url;
      const config: AxiosRequestConfig = {
        method,
        url: fullUrl,
        data: { reqData: encryptedData, _csrf: csrfToken },
        headers: {
          reqKey: salt,
          pageURL: globalThis.window === undefined ? "" : globalThis.window.location.href,
          "Content-Type": "application/json",
          ...headers,
        },
        signal,
        withCredentials: true,
      };

      const response = await axios(config);
      const decryptedRes = await decryptResponse(response);

      if (decryptedRes?.status === "success") {
        if (
          params.storeResponseInLocal &&
          params.storageKey &&
          typeof localStorage !== "undefined"
        ) {
          localStorage.setItem(params.storageKey, JSON.stringify({ rK: salt, rD: response.data }));
        }
        return decryptedRes;
      }
      if (decryptedRes?.status === "error") {
        throw decryptedRes;
      }
      return decryptedRes;
    } catch (err) {
      return handleEncryptedError(err, csrfToken, callAPI);
    }
  };

  const csrfToken = await resolveCsrfToken();
  return callAPI(csrfToken);
};
